import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConsultaAlertas, ConsultaInternas } from '../lib/api';
import { GlobalContext } from '../lib/GlobalVar';
import { montaCatalogo, getFixadas, getRecentes, toggleFixada, isFixada, pushRecente, labelDe, iconeDe } from '../lib/telas';
import Icon from './Icon';

const LABELS = {
    '/Dashboard':     'Dashboard',
    '/Crontab':       'Crontab Visual',
    '/Internas':      'Páginas Internas',
    '/Arquivos':      'Acesso a Arquivos',
    '/Logs':          'Logs & Histórico',
    '/Alertas':       'Alertas',
    '/Configuracoes': 'Configurações',
    '/TI/Logins':     'Logins & Usuários',
    '/TI/Permissoes': 'Permissões de Telas',
    '/TI/Bancos':     'Conexões de Banco',
    '/TI/Construtor': 'Construtor de API',
    '/TI/Telegram':   'Telegram & Notificações'
};

const POLL_MS = 30_000; // refresca contagem de notificacoes a cada 30s
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || '');
const MOD = isMac ? '⌘' : 'Ctrl';
const SHORTCUT_LABEL = isMac ? '⌘K' : 'Ctrl K';

function buildShortcuts(navigate, focar) {
    return [
        { combo: `${MOD}+K`,  label: 'Focar a busca de telas',             action: focar },
        { combo: 'Esc',       label: 'Fechar popovers e modais',            action: null },
        { combo: '↑ ↓ Enter', label: 'Navegar/abrir na paleta de telas',    action: focar },
        { combo: `${MOD}+D`,  label: 'Ir para Dashboard',                   action: () => navigate('/Dashboard') },
        { combo: `${MOD}+L`,  label: 'Ir para Logs & Histórico',            action: () => navigate('/Logs') },
        { combo: `${MOD}+J`,  label: 'Ir para Alertas',                     action: () => navigate('/Alertas') },
        { combo: `${MOD}+,`,  label: 'Ir para Configurações pessoais',      action: () => navigate('/Configuracoes') },
        { combo: '?',         label: 'Mostrar esta lista de atalhos',       action: null }
    ];
}

export default function Topbar({ onToggleSidebar }) {
    const loc = useLocation();
    const navigate = useNavigate();
    const { user } = useContext(GlobalContext);
    const userId = (user && (user.id || user.email)) || 'anon';
    const isIT = loc.pathname.startsWith('/TI/');
    const current = LABELS[loc.pathname] || '';

    const inputRef = useRef(null);
    const searchRef = useRef(null);
    const popRef = useRef(null);
    const bellRef = useRef(null);
    const shortcutsRef = useRef(null);
    const shortcutsBtnRef = useRef(null);

    const [busca, setBusca] = useState('');
    const [alertas, setAlertas] = useState([]);
    const [popAberto, setPopAberto] = useState(false);
    const [shortcutsAberto, setShortcutsAberto] = useState(false);

    // --- Paleta de telas ---
    const [paletaAberta, setPaletaAberta] = useState(false);
    const [internas, setInternas] = useState([]);
    const [fixadas, setFixadas] = useState(() => getFixadas(userId));
    const [recentes, setRecentes] = useState(() => getRecentes(userId));
    const [idx, setIdx] = useState(0);

    const catalogo = useMemo(() => montaCatalogo(user, internas), [user, internas]);

    function abrirBusca() {
        setShortcutsAberto(false);
        setPaletaAberta(true);
        setFixadas(getFixadas(userId));
        setRecentes(getRecentes(userId));
        if (inputRef.current) inputRef.current.focus();
    }

    // Carrega paginas internas uma vez (entram no catalogo de busca).
    useEffect(() => {
        let alive = true;
        ConsultaInternas({}).then(r => {
            if (!alive) return;
            setInternas(Array.isArray(r) ? r : (r && r.telas) || []);
        }).catch(() => {});
        return () => { alive = false; };
    }, []);

    // Rastreia a tela atual como "recente" (as 7 ultimas que o usuario mexeu).
    useEffect(() => {
        const to = loc.pathname;
        if (!to || to === '/') return;
        const nova = pushRecente(userId, { to, label: labelDe(to, catalogo) || LABELS[to] || to, icon: iconeDe(to, catalogo) });
        setRecentes(nova);
    }, [loc.pathname, catalogo, userId]);

    // --- Atalhos globais ---
    useEffect(() => {
        function onKey(e) {
            const k = (e.key || '').toLowerCase();
            const mod = e.ctrlKey || e.metaKey;
            if (mod && k === 'k') { e.preventDefault(); abrirBusca(); return; }
            if (mod && k === 'd') { e.preventDefault(); navigate('/Dashboard'); return; }
            if (mod && k === 'l') { e.preventDefault(); navigate('/Logs'); return; }
            if (mod && k === 'j') { e.preventDefault(); navigate('/Alertas'); return; }
            if (mod && k === ',') { e.preventDefault(); navigate('/Configuracoes'); return; }
            if (e.key === '?' && !e.target.matches?.('input, textarea')) {
                e.preventDefault();
                setShortcutsAberto(v => !v);
            }
            if (e.key === 'Escape') {
                if (paletaAberta) setPaletaAberta(false);
                if (popAberto) setPopAberto(false);
                if (shortcutsAberto) setShortcutsAberto(false);
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [popAberto, shortcutsAberto, paletaAberta, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Carrega alertas (poll suave) ---
    async function carregarAlertas() {
        const r = await ConsultaAlertas({});
        if (Array.isArray(r)) setAlertas(r);
    }
    useEffect(() => {
        let alive = true;
        carregarAlertas();
        const id = setInterval(() => { if (alive) carregarAlertas(); }, POLL_MS);
        function onResolved() { carregarAlertas(); }
        window.addEventListener('centralbabita:alertas-atualizados', onResolved);
        return () => {
            alive = false;
            clearInterval(id);
            window.removeEventListener('centralbabita:alertas-atualizados', onResolved);
        };
    }, []);

    // --- Fecha popovers ao clicar fora ---
    useEffect(() => {
        function onClickFora(e) {
            if (paletaAberta && searchRef.current && !searchRef.current.contains(e.target)) {
                setPaletaAberta(false);
            }
            if (popAberto) {
                if (popRef.current && popRef.current.contains(e.target)) return;
                if (bellRef.current && bellRef.current.contains(e.target)) return;
                setPopAberto(false);
            }
            if (shortcutsAberto) {
                if (shortcutsRef.current && shortcutsRef.current.contains(e.target)) return;
                if (shortcutsBtnRef.current && shortcutsBtnRef.current.contains(e.target)) return;
                setShortcutsAberto(false);
            }
        }
        window.addEventListener('mousedown', onClickFora);
        return () => window.removeEventListener('mousedown', onClickFora);
    }, [popAberto, shortcutsAberto, paletaAberta]);

    // Lista exibida na paleta: com busca = filtrado; sem busca = fixadas + recentes (sem repetir as fixadas).
    const q = busca.trim().toLowerCase();
    const resultados = q
        ? catalogo.filter(t => (t.label || '').toLowerCase().includes(q) || (t.to || '').toLowerCase().includes(q)).slice(0, 12)
        : [];
    const recentesVis = recentes.filter(r => !fixadas.some(f => f.to === r.to));
    const listaNavegavel = q ? resultados : [...fixadas, ...recentesVis];

    function irPara(t) {
        if (!t) return;
        setPaletaAberta(false);
        setBusca('');
        if (t.externo && /^https?:/i.test(t.to)) { window.open(t.to, '_blank'); return; }
        navigate(t.to);
    }

    function onPin(e, t) {
        e.preventDefault();
        e.stopPropagation();
        const nova = toggleFixada(userId, t);
        setFixadas(nova);
    }

    function onKeyDownBusca(e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, Math.max(listaNavegavel.length - 1, 0))); return; }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); return; }
        if (e.key === 'Enter') {
            e.preventDefault();
            const alvo = listaNavegavel[idx] || listaNavegavel[0];
            if (alvo) return irPara(alvo);
            // fallback: sem tela correspondente, mantem a busca antiga por conteudo
            if (q) navigate(`/Logs?q=${encodeURIComponent(busca.trim())}`);
            return;
        }
    }

    const naoLidas = alertas.length;
    const ultimas = alertas.slice(0, 5);
    const shortcuts = buildShortcuts(navigate, abrirBusca);

    // Linha da paleta (item de tela) com estrela de fixar.
    const Linha = (t, posicao) => {
        const ativo = posicao === idx;
        const fix = isFixada(userId, t.to);
        return (
            <button
                key={t.to}
                type="button"
                onMouseEnter={() => setIdx(posicao)}
                onClick={() => irPara(t)}
                style={{
                    display: 'grid', gridTemplateColumns: '20px 1fr auto', gap: 10, alignItems: 'center',
                    width: '100%', padding: '9px 16px', border: 0, textAlign: 'left', cursor: 'pointer',
                    background: ativo ? 'var(--line-soft, #f3efe9)' : 'transparent',
                    borderBottom: '1px dashed var(--line-soft, var(--line))'
                }}
            >
                <span style={{ display: 'inline-flex' }}><Icon name={t.icon || 'pages'} size={15} /></span>
                <span style={{ fontSize: 13, color: 'var(--ink)' }}>{t.label}</span>
                <span
                    role="button"
                    title={fix ? 'Desafixar' : 'Fixar'}
                    onClick={(e) => onPin(e, t)}
                    style={{ display: 'inline-flex', padding: 2, cursor: 'pointer', color: fix ? 'var(--ink)' : 'var(--ink-mute)' }}
                >
                    <Icon name={fix ? 'check' : 'plus'} size={13} />
                </span>
            </button>
        );
    };

    return (
        <header className="topbar">
            <button
                className="icon-btn"
                title="Colapsar / expandir menu"
                onClick={onToggleSidebar}
                style={{ marginRight: 4 }}
            ><Icon name="menu" size={16}/></button>

            <div className="crumbs">
                <span>Babita</span>
                <span className="sep">/</span>
                <span>Central</span>
                {isIT && <><span className="sep">/</span><span style={{ color: 'var(--ink)' }}>TI</span></>}
                <span className="sep">/</span>
                <span className="current">{current}</span>
            </div>

            <div className="topbar-spacer"></div>

            <div className="topbar-actions">
                <div className="search" ref={searchRef} style={{ position: 'relative' }}>
                    <Icon name="search" size={14}/>
                    <input
                        ref={inputRef}
                        placeholder="Buscar telas, fixar, recentes…"
                        value={busca}
                        onChange={e => { setBusca(e.target.value); setIdx(0); setPaletaAberta(true); }}
                        onFocus={abrirBusca}
                        onKeyDown={onKeyDownBusca}
                    />
                    <button
                        ref={shortcutsBtnRef}
                        type="button"
                        className="search-kbd"
                        title={`Ver atalhos · ${SHORTCUT_LABEL}`}
                        onClick={() => { setPaletaAberta(false); setShortcutsAberto(v => !v); }}
                        style={{
                            fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-mute)',
                            background: 'transparent', border: '1px solid var(--line)', padding: '2px 7px',
                            borderRadius: 0, cursor: 'pointer', letterSpacing: '0.06em'
                        }}
                    >{SHORTCUT_LABEL}</button>

                    {paletaAberta && (
                        <div
                            className="paleta-pop"
                            style={{
                                position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
                                background: 'var(--paper)', border: '1px solid var(--line)',
                                boxShadow: '0 8px 32px -16px rgba(0,0,0,.25)', zIndex: 60, maxHeight: 420, overflow: 'auto'
                            }}
                        >
                            {q ? (
                                <>
                                    <div style={{ padding: '10px 16px 6px' }} className="eyebrow">Resultados</div>
                                    {resultados.length === 0 && (
                                        <div style={{ padding: '4px 16px 14px', fontSize: 12, color: 'var(--ink-mute)' }}>
                                            Nenhuma tela encontrada. <span className="mono">Enter</span> busca nos Logs.
                                        </div>
                                    )}
                                    {resultados.map((t, i) => Linha(t, i))}
                                </>
                            ) : (
                                <>
                                    <div style={{ padding: '10px 16px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span className="eyebrow">Fixadas</span>
                                        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-mute)' }}>{fixadas.length}</span>
                                    </div>
                                    {fixadas.length === 0 && (
                                        <div style={{ padding: '2px 16px 10px', fontSize: 11.5, color: 'var(--ink-mute)' }}>
                                            Use a estrela <Icon name="plus" size={11}/> ao lado de uma tela para fixá-la aqui.
                                        </div>
                                    )}
                                    {fixadas.map((t, i) => Linha(t, i))}

                                    <div style={{ padding: '10px 16px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span className="eyebrow">Recentes</span>
                                        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-mute)' }}>últimas {Math.min(recentesVis.length, 7)}</span>
                                    </div>
                                    {recentesVis.length === 0 && (
                                        <div style={{ padding: '2px 16px 14px', fontSize: 11.5, color: 'var(--ink-mute)' }}>
                                            Conforme você navega, as últimas telas aparecem aqui.
                                        </div>
                                    )}
                                    {recentesVis.map((t, i) => Linha(t, fixadas.length + i))}
                                </>
                            )}
                        </div>
                    )}

                    {shortcutsAberto && (
                        <div
                            ref={shortcutsRef}
                            className="shortcuts-pop"
                            style={{
                                position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 360,
                                background: 'var(--paper)', border: '1px solid var(--line)',
                                boxShadow: '0 8px 32px -16px rgba(0,0,0,.25)', zIndex: 60
                            }}
                        >
                            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line-soft, var(--line))', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div className="eyebrow">Atalhos de teclado</div>
                                <span className="mono" style={{ fontSize: 11, color: 'var(--ink-mute)' }}>{shortcuts.length} atalhos</span>
                            </div>
                            <div>
                                {shortcuts.map((s, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => { if (s.action) { s.action(); setShortcutsAberto(false); } }}
                                        disabled={!s.action}
                                        style={{
                                            display: 'grid', gridTemplateColumns: '90px 1fr', gap: 12, alignItems: 'center',
                                            width: '100%', padding: '10px 18px', background: 'transparent', border: 0,
                                            borderBottom: '1px dashed var(--line-soft, var(--line))', textAlign: 'left',
                                            cursor: s.action ? 'pointer' : 'default', opacity: s.action ? 1 : 0.75
                                        }}
                                    >
                                        <span className="mono" style={{ fontSize: 11, color: 'var(--ink)', border: '1px solid var(--line)', padding: '2px 6px', letterSpacing: '0.06em', textAlign: 'center' }}>{s.combo}</span>
                                        <span style={{ fontSize: 12.5, color: 'var(--ink)' }}>{s.label}</span>
                                    </button>
                                ))}
                            </div>
                            <div style={{ padding: '10px 18px', fontSize: 10.5, color: 'var(--ink-mute)', textAlign: 'center' }}>
                                Pressione <span className="mono">?</span> para abrir/fechar esta lista.
                            </div>
                        </div>
                    )}
                </div>

                <div ref={bellRef} style={{ position: 'relative' }}>
                    <button
                        className="icon-btn"
                        title={naoLidas > 0 ? `${naoLidas} notificação(ões) não lida(s)` : 'Notificações'}
                        onClick={() => setPopAberto(v => !v)}
                        style={{ position: 'relative' }}
                    >
                        <Icon name="bell" size={16}/>
                        {naoLidas > 0 && (
                            <span
                                aria-label="não lidas"
                                style={{
                                    position: 'absolute', top: 4, right: 4, minWidth: 14, height: 14, padding: '0 4px',
                                    background: 'var(--status-err, #8E2F2F)', color: '#fff', fontSize: 9,
                                    fontFamily: 'var(--font-mono)', lineHeight: '14px', textAlign: 'center',
                                    borderRadius: 8, boxShadow: '0 0 0 2px var(--paper, #fff)', letterSpacing: 0
                                }}
                            >{naoLidas > 9 ? '9+' : naoLidas}</span>
                        )}
                    </button>

                    {popAberto && (
                        <div
                            ref={popRef}
                            className="notif-pop"
                            style={{
                                position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 360,
                                background: 'var(--paper)', border: '1px solid var(--line)',
                                boxShadow: '0 8px 32px -16px rgba(0,0,0,.25)', zIndex: 50
                            }}
                        >
                            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line-soft, var(--line))', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div className="eyebrow"><span className="num">06</span> Notificações</div>
                                <span className="mono" style={{ fontSize: 11, color: 'var(--ink-mute)' }}>
                                    {naoLidas} não lida{naoLidas === 1 ? '' : 's'}
                                </span>
                            </div>

                            {ultimas.length === 0 && (
                                <div style={{ padding: 24, color: 'var(--ink-mute)', fontSize: 12.5, textAlign: 'center' }}>
                                    Nenhuma notificação ativa.
                                </div>
                            )}

                            {ultimas.map(a => (
                                <button
                                    key={a.id}
                                    type="button"
                                    onClick={() => { setPopAberto(false); navigate('/Alertas'); }}
                                    style={{
                                        display: 'grid', gridTemplateColumns: '8px 1fr auto', gap: 10, alignItems: 'start',
                                        width: '100%', padding: '12px 18px', background: 'transparent', border: 0,
                                        borderBottom: '1px dashed var(--line-soft, var(--line))', textAlign: 'left', cursor: 'pointer'
                                    }}
                                >
                                    <span className={'dot ' + (a.severidade === 'err' ? 'err' : a.severidade === 'warn' ? 'paused' : 'ok')} style={{ marginTop: 6 }}></span>
                                    <div>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.35 }}>
                                            {a.titulo || a.msg || '(sem título)'}
                                        </div>
                                        <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-mute)', marginTop: 3 }}>
                                            {a.origem || '-'} · {a.usuario || '-'}
                                        </div>
                                    </div>
                                    <span className="mono" style={{ fontSize: 10, color: 'var(--ink-mute)' }}>
                                        {(a.ts || '').split(',')[1]?.trim() || a.ts}
                                    </span>
                                </button>
                            ))}

                            <button
                                type="button"
                                onClick={() => { setPopAberto(false); navigate('/Alertas'); }}
                                style={{
                                    width: '100%', padding: '12px 18px', background: 'transparent', border: 0,
                                    fontFamily: 'var(--font-ui)', fontSize: 11, letterSpacing: '0.22em',
                                    textTransform: 'uppercase', color: 'var(--ink)', cursor: 'pointer', textAlign: 'center'
                                }}
                            >Ver todas →</button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
